import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../services/api';
import { useApiData } from '../hooks/useApiData';
import { useTier } from '../context/TierContext';
import {
  ScoreCard,
  ClientCell,
  FlagsCell,
  FilterChips,
  SeriesBars,
  LoadingState,
  ErrorState,
  EmptyState
} from '../components/primitives';
import RangeControl, { DEFAULT_RANGE, rangeLabel } from '../components/RangeControl';
import FailedClientsNote from '../components/FailedClientsNote';
import UsageReportButton from '../components/UsageReportButton';
import { IS_PROD_TIER_SERVED } from '../constants/tenantTiers';
import { codeOf, fmtInt, fmtMoney } from '../lib/format';
import { collectFlagFilters, filterClientsByFlags, sortClientsByName, NameSortDir } from '../lib/selectors';
import { TimeRange, SeriesPoint, Activity } from '../types/superadmin';

type SeriesField = 'minutes' | 'sessions' | 'newUsers';

export default function Dashboard() {
  const [range, setRange] = useState<TimeRange>(DEFAULT_RANGE);
  const { tier } = useTier();
  // Current state (totals, at-risk, tickets) and range activity load separately:
  // changing the range refetches only the activity half.
  const summaryState = useApiData(() => api.getDashboardSummary(tier), [tier]);
  const activityState = useApiData(
    () => api.getDashboardActivity(range, tier),
    [JSON.stringify(range), tier]
  );
  const [riskFilter, setRiskFilter] = useState<Set<string>>(new Set());
  // Default order is by risk (backend-sorted); the Client header sorts by name.
  const [nameDir, setNameDir] = useState<NameSortDir>(null);
  const [expanded, setExpanded] = useState<{ label: string; field: SeriesField; unit?: string } | null>(null);

  const d = summaryState.data?.data;
  const rangeData = activityState.data?.data;

  const { filterOptions, filterCounts } = useMemo(
    () => collectFlagFilters(d?.atRiskClients || []),
    [d]
  );

  const filteredAtRisk = useMemo(
    () => filterClientsByFlags(d?.atRiskClients || [], riskFilter),
    [d, riskFilter]
  );

  // Risk order by default (backend-sorted); alphabetical when the Client header
  // is clicked (asc -> desc -> back to risk order).
  const sortedAtRisk = useMemo(
    () => sortClientsByName(filteredAtRisk, nameDir),
    [filteredAtRisk, nameDir]
  );

  const cycleNameSort = () =>
    setNameDir((prev) => (prev === null ? 'asc' : prev === 'asc' ? 'desc' : null));
  const nameArrow = nameDir === 'asc' ? '↑' : nameDir === 'desc' ? '↓' : '↕';

  if (summaryState.loading) return <LoadingState />;
  if (summaryState.error || !d) return <ErrorState message={summaryState.error || 'No data available.'} />;

  const { current } = d;
  // Range cards show a dash until the activity half arrives.
  const activity: Partial<Activity> = rangeData?.activity ?? {};
  const series = rangeData?.series ?? [];
  const seriesVals = (field: SeriesField) => series.map((s: SeriesPoint) => s[field]);
  const rangeValue = (value: number | undefined) => (value === undefined ? '—' : fmtInt(value));
  const today = new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });

  const toggle = (id: string) =>
    setRiskFilter((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  return (
    <>
      <div className="head">
        <div>
          <div className="eyebrow">Home · {today}</div>
          <h1 className="title">
            <span className="serif">
              {current.atRiskCount} client{current.atRiskCount === 1 ? '' : 's'} need a closer look
            </span>
            <br />
            <span className="serif" style={{ color: 'var(--ink-muted)' }}>
              across {current.totalClients} live and {fmtInt(current.totalUsers)} users.
            </span>
          </h1>
          <p className="subtitle">
            Activity below covers <b>{rangeLabel(range)}</b>. At-risk, billing and totals reflect
            current state.
          </p>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, alignItems: 'flex-end' }}>
          <RangeControl value={range} onChange={setRange} />
          {IS_PROD_TIER_SERVED && <UsageReportButton range={range} />}
          <span className="freshness">Live</span>
        </div>
      </div>

      <FailedClientsNote failed={[...(summaryState.data?.meta?.failed ?? []), ...(activityState.data?.meta?.failed ?? [])]} />
      {activityState.error && <ErrorState message={activityState.error} />}

      {/* Scorecard: current state + range activity */}
      <div className="scorecard seven">
        <ScoreCard label="Total clients" value={fmtInt(current.totalClients)} sub="Active clients." />
        <ScoreCard
          label="Total users"
          value={fmtInt(current.totalUsers)}
          sub={`${rangeValue(activity.newUsers)} new · ${rangeLabel(range)}`}
        />
        <ScoreCard label="MRR" prefix="$" value={fmtInt(current.mrr)} sub={`${fmtMoney(current.revenueLifetime)} lifetime`} />
        <ScoreCard
          label="Call minutes"
          value={rangeValue(activity.minutes)}
          sparkData={seriesVals('minutes')}
          sparkLabel={rangeLabel(range)}
          sparkTone="pos"
          sub={`${rangeLabel(range)}`}
          onExpand={() => setExpanded({ label: 'Call minutes', field: 'minutes', unit: ' min' })}
        />
        <ScoreCard
          label="Sessions"
          value={rangeValue(activity.sessions)}
          sparkData={seriesVals('sessions')}
          sparkLabel={rangeLabel(range)}
          sub={activity.callQualityScore != null ? `${activity.callQualityScore}% call quality` : 'no analyzed calls'}
          onExpand={() => setExpanded({ label: 'Sessions', field: 'sessions' })}
        />
        <ScoreCard
          label="Training quality"
          value={activity.trainingQualityScore == null ? '—' : activity.trainingQualityScore}
          unit={activity.trainingQualityScore == null ? undefined : '%'}
          sub={
            rangeData
              ? activity.trainingSessions
                ? `${activity.trainingCompletion ?? 0}% completion · ${fmtInt(activity.trainingSessions ?? 0)} sessions`
                : 'no training in range'
              : rangeLabel(range)
          }
        />
        <ScoreCard label="At-risk clients" value={fmtInt(current.atRiskCount)} sub={`${fmtInt(current.openTickets)} open tickets`} />
      </div>

      {expanded && (
        <section className="section">
          <div className="section-head">
            <div>
              <h3>{expanded.label} · by day</h3>
              <p className="desc">{rangeLabel(range)}, across all clients.</p>
            </div>
            <div className="right">
              <button className="btn" onClick={() => setExpanded(null)}>
                Close ×
              </button>
            </div>
          </div>
          <SeriesBars data={series} field={expanded.field} unit={expanded.unit} />
        </section>
      )}

      {/* At-risk clients (current state) */}
      <section className="section">
        <div className="section-head">
          <div>
            <h3>At-risk clients</h3>
            <p className="desc">
              Current health: billing, KB, inactive users, and calls, training and call quality over the last 30
              days. Click to drill in.
            </p>
          </div>
        </div>
        {filterOptions.length > 0 && (
          <FilterChips
            label="Red flags"
            options={filterOptions}
            counts={filterCounts}
            selected={riskFilter}
            onToggle={toggle}
            onClear={() => setRiskFilter(new Set())}
          />
        )}
        <div className="tbl">
          <table>
            <thead>
              <tr>
                <th
                  style={{ width: '28%' }}
                  className={'sortable' + (nameDir ? ' sorted' : '')}
                  onClick={cycleNameSort}
                >
                  Client <span className="sort-arr">{nameArrow}</span>
                </th>
                <th>Red flags</th>
                <th style={{ width: 120, textAlign: 'right' }}>Drill in</th>
              </tr>
            </thead>
            <tbody>
              {sortedAtRisk.map((c) => (
                <tr key={c.tenantId}>
                  <td>
                    <ClientCell
                      name={c.displayName}
                      code={codeOf(c.tenantId)}
                      sub={`${fmtInt(c.users)} users · ${c.subscriptionStatus}`}
                    />
                  </td>
                  <td>
                    <FlagsCell flags={c.flags} />
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    <Link className="action-btn" to={`/clients/${c.tenantId}`}>
                      View →
                    </Link>
                  </td>
                </tr>
              ))}
              {filteredAtRisk.length === 0 && (
                <tr>
                  <td colSpan={3}>
                    <EmptyState>
                      No clients match the selected flags. <b>Clear filters</b> to see all.
                    </EmptyState>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      {/* Top clients (range) + open tickets (current) */}
      <section className="section">
        <div className="split">
          <div>
            <div className="section-head">
              <div>
                <h3>Top clients</h3>
                <p className="desc">Most active by call minutes, {rangeLabel(range)}.</p>
              </div>
            </div>
            <div className="tbl">
              <table>
                <thead>
                  <tr>
                    <th style={{ width: '50%' }}>Client</th>
                    <th>Activity</th>
                    <th style={{ width: 90, textAlign: 'right' }}>View</th>
                  </tr>
                </thead>
                <tbody>
                  {activityState.loading && (
                    <tr>
                      <td colSpan={3}>
                        <LoadingState label="Loading activity…" />
                      </td>
                    </tr>
                  )}
                  {(rangeData?.topClients ?? []).map((c) => (
                    <tr key={c.tenantId}>
                      <td>
                        <ClientCell name={c.displayName} code={codeOf(c.tenantId)} />
                      </td>
                      <td>
                        <div className="flags">
                          <span className="chip pos">{fmtInt(c.minutes)} min</span>
                          <span className="chip info">{fmtInt(c.sessions)} sessions</span>
                        </div>
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        <Link className="action-btn" to={`/clients/${c.tenantId}`}>
                          →
                        </Link>
                      </td>
                    </tr>
                  ))}
                  {!activityState.loading && (rangeData?.topClients ?? []).length === 0 && (
                    <tr>
                      <td colSpan={3}>
                        <EmptyState>No activity in this range.</EmptyState>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div>
            <div className="section-head">
              <div>
                <h3>Open tickets</h3>
                <p className="desc">Oldest first, current.</p>
              </div>
            </div>
            <div className="rank">
              {d.openTickets.length === 0 && (
                <EmptyState>
                  <b>No open tickets.</b> All clear.
                </EmptyState>
              )}
              {d.openTickets.map((ticket, i) => (
                <div className="rank-row" key={ticket.ticketId}>
                  <div className="pos">{String(i + 1).padStart(2, '0')}</div>
                  <div>
                    <div className="name">{ticket.title}</div>
                    <div className="meta">
                      {ticket.displayName} · {ticket.name || ticket.email || 'unknown'}
                    </div>
                  </div>
                  <div className="stat">
                    <span className={'chip ' + (ticket.severity === 'high' ? 'neg' : ticket.severity === 'medium' ? 'warn' : '')}>
                      {ticket.severity}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
